import { ReactNode, PointerEvent, useEffect, useLayoutEffect, useRef, useState } from "react"
import { Text } from "../../../components"
import { resolveAssetUrl } from "../../../helpers"
import { SHOW_POINT } from "../../../config";
import { trackMapClick } from "../../get-qr/components/link-row";

export interface MapPlace {
    storeId: number;
    title: string;
    subtitle: string;
    image?: string;
    // null — оценок пока меньше порога, бэк рейтинг не отдаёт
    rating: number | null;
    // Сколько баллов можно получить у места и сколько потратить; 0 — не участвует
    obtainingPoints: number;
    spendingPoints: number;
    lat: number;
    lng: number;
}

export type LatLng = {lat: number; lng: number}

// Оба флага — места, где можно и получить, и потратить (points=both на бэке)
export interface PointsFilter {
    obtaining: boolean;
    spending: boolean;
}

// На сколько пикселей нужно потянуть шторку, чтобы она сменила состояние
const DRAG_THRESHOLD = 40
// Сдвиг пальца, после которого нажатие считается перетаскиванием, а не тапом
const DRAG_START = 6

// Расстояние по прямой (формула гаверсинусов), в метрах
export const distanceMeters = (a: LatLng, b: LatLng): number => {
    const R = 6371000
    const toRad = (deg: number) => deg * Math.PI / 180
    const dLat = toRad(b.lat - a.lat)
    const dLng = toRad(b.lng - a.lng)
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2
    return 2 * R * Math.asin(Math.sqrt(h))
}

export const formatDistance = (meters: number): string =>
    meters < 1000
        ? `${Math.round(meters / 10) * 10} м`
        : `${(meters / 1000).toFixed(1).replace('.', ',')} км`

const pluralRu = (count: number, [one, few, many]: [string, string, string]) => {
    const rule = new Intl.PluralRules('ru-RU').select(count)
    return rule === 'one' ? one : rule === 'few' ? few : many
}

const pointsWord = (count: number) => pluralRu(count, ['балл', 'балла', 'баллов'])

const STARS = [1, 2, 3, 4, 5]

const StarIcon = () => (
    <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true">
        <path d="M12 2.5l2.94 5.96 6.56.95-4.75 4.63 1.12 6.54L12 17.5l-5.87 3.08 1.12-6.54L2.5 9.41l6.56-.95z" />
    </svg>
)

const PlusIcon = () => (
    <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <path d="M8 3v10M3 8h10" />
    </svg>
)

const TagIcon = () => (
    <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" aria-hidden="true">
        <path d="M2 2h5.5L14 8.5 8.5 14 2 7.5z" />
        <circle cx="5" cy="5" r="1" fill="currentColor" stroke="none" />
    </svg>
)

const LocateIcon = () => (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
    </svg>
)

// Только показ рейтинга, без голосования — оценивают на странице заведения
export const PlaceRating = ({rating}: {rating: number | null}) => (
    <div className="place-rating">
        {rating === null ? (
            <span className="place-rating-value">Мало оценок</span>
        ) : (
            <>
                <div className="place-rating-stars" aria-label={`Рейтинг ${rating.toFixed(1)} из 5`}>
                    {STARS.map((star) => (
                        <span key={star} className="place-rating-star">
                            <span className="place-rating-star-empty"><StarIcon /></span>
                            <span
                                className="place-rating-star-fill"
                                style={{width: `${Math.min(Math.max(rating - star + 1, 0), 1) * 100}%`}}
                            >
                                <StarIcon />
                            </span>
                        </span>
                    ))}
                </div>
                <span className="place-rating-value">{rating.toFixed(1).replace('.', ',')}</span>
            </>
        )}
    </div>
)

// Компактные бейджи баллов для строки списка
const PointsChips = ({place}: {place: MapPlace}) => (
    <>
        {place.obtainingPoints > 0 && (
            <span className="place-points-chip is-obtain"><PlusIcon />{place.obtainingPoints}</span>
        )}
        {place.spendingPoints > 0 && (
            <span className="place-points-chip is-spend"><TagIcon />{place.spendingPoints}</span>
        )}
    </>
)

// Крупные плитки баллов для карточки выбранного места
const PointsTiles = ({place}: {place: MapPlace}) => {
    if (place.obtainingPoints <= 0 && place.spendingPoints <= 0) return null
    return (
        <div className="place-points-tiles">
            {place.obtainingPoints > 0 && (
                <div className="place-points-tile is-obtain">
                    <span className="place-points-tile-icon"><PlusIcon /></span>
                    <span className="place-points-tile-value">{place.obtainingPoints}</span>
                    <span className="place-points-tile-label">{pointsWord(place.obtainingPoints)} можно получить</span>
                </div>
            )}
            {place.spendingPoints > 0 && (
                <div className="place-points-tile is-spend">
                    <span className="place-points-tile-icon"><TagIcon /></span>
                    <span className="place-points-tile-value">{place.spendingPoints}</span>
                    <span className="place-points-tile-label">{pointsWord(place.spendingPoints)} можно потратить</span>
                </div>
            )}
        </div>
    )
}

// Баллы пользователя; пока грузятся — скелетон, чтобы шапка не прыгала
const UserPointsPill = ({points}: {points: number | null}) => {
    if (!SHOW_POINT) {
        return
    }

    return (
    <span className={`map-user-points${points === null ? ' is-loading' : ''}`} aria-label={points === null ? 'Загружаем баллы' : `У вас ${points} ${pointsWord(points)}`}>
        <span className="map-user-points-icon"><StarIcon /></span>
        <span className="map-user-points-text">
            <span className="map-user-points-value">{points ?? '—'}</span>
            <span className="map-user-points-label">{points === null ? 'баллов' : pointsWord(points)}</span>
        </span>
    </span>
)}

const PlaceAvatar = ({title, image, large}: Pick<MapPlace, 'title' | 'image'> & {large?: boolean}) => (
    <div className={`places-sheet-avatar${large ? ' is-large' : ''}`}>
        {image
            ? <img src={resolveAssetUrl(image)} alt={title} />
            : <span>{title.match(/[a-zа-яё0-9]/i)?.[0].toUpperCase()}</span>}
    </div>
)

interface PlaceCardProps {
    place: MapPlace;
    distance: number | null;
    userPoints: number | null;
    onClose: () => void;
}

const PlaceCard = ({place, distance, userPoints, onClose}: PlaceCardProps) => (
    <div className="map-place-card">
        <div className="map-place-card-top">
            <PlaceAvatar title={place.title} image={place.image} large />
            <div className="places-sheet-item-text">
                <Text size="l" color="white">{place.title}</Text>
                {place.subtitle && <Text size="xs" color="lightGray">{place.subtitle}</Text>}
                <div className="map-place-card-meta">
                    <PlaceRating rating={place.rating} />
                    {distance !== null && <span className="places-sheet-distance">{formatDistance(distance)}</span>}
                </div>
            </div>
            <button type="button" className="map-sheet-close" aria-label="Закрыть" onClick={onClose}>
                <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                    <path d="M4 4l8 8M12 4l-8 8" />
                </svg>
            </button>
        </div>

        <PointsTiles place={place} />

        {place.spendingPoints > 0 && !!userPoints && (
            <div className="map-place-card-hint">
                <span className="map-user-points-icon"><StarIcon /></span>
                <Text size="xs" color="lightGray">{`У вас ${userPoints} ${pointsWord(userPoints)} — их можно потратить здесь`}</Text>
            </div>
        )}

        <div className="places-sheet-item-actions">
            {/* <a
                className="places-sheet-button"
                href={`https://yandex.ru/maps/?rtext=~${place.lat},${place.lng}`}
                target="_blank"
                rel="noreferrer"
            >
                Маршрут
            </a> */}
            <a className="places-sheet-button is-primary" href={`/${place.storeId}`} onClick={() => trackMapClick(place.storeId)}>
                Открыть страницу
            </a>
        </div>
    </div>
)

interface FiltersProps {
    searchField: ReactNode;
    value: PointsFilter;
    onChange: (value: PointsFilter) => void;
}

const Filters = ({value, onChange, searchField}: FiltersProps) => {
    const all = !value.obtaining && !value.spending
    return (
        <div className="map-filters">
            <button
                type="button"
                className={`map-filter${all ? ' is-active' : ''}`}
                onClick={() => onChange({obtaining: false, spending: false})}
            >
                Все места
            </button>
            {searchField}
            {
            SHOW_POINT && <>
                <button
                    type="button"
                    className={`map-filter is-obtain${value.obtaining ? ' is-active' : ''}`}
                    onClick={() => onChange({...value, obtaining: !value.obtaining})}
                >
                    <PlusIcon />Получить баллы
                </button>
                <button
                    type="button"
                    className={`map-filter is-spend${value.spending ? ' is-active' : ''}`}
                    onClick={() => onChange({...value, spending: !value.spending})}
                >
                    <TagIcon />Потратить баллы
                </button>
            </>
            }
        </div>
    )
}

interface MapSheetProps {
    searchField: ReactNode;
    places: MapPlace[];
    selected: MapPlace | null;
    userLocation: LatLng | null;
    userPoints: number | null;
    loading: boolean;
    filter: PointsFilter;
    onFilterChange: (value: PointsFilter) => void;
    onSelect: (place: MapPlace) => void;
    onDeselect: () => void;
    onLocate: () => void;
}

// Шторка снизу, как в обычных картах: свёрнута — заголовок и фильтры, развёрнута — список,
// выбрано место — его карточка
export const MapSheet = ({places, selected, userLocation, userPoints, loading, filter, searchField, onFilterChange, onSelect, onDeselect, onLocate}: MapSheetProps) => {
    const [expanded, setExpanded] = useState(false)
    const [dragY, setDragY] = useState(0)
    const [sizes, setSizes] = useState({sheet: 0, peek: 0})
    const dragStart = useRef<number | null>(null)
    const dragging = useRef(false)
    const sheetRef = useRef<HTMLDivElement>(null)
    const peekRef = useRef<HTMLDivElement>(null)

    // Высота свёрнутого состояния зависит от контента шапки — меряем её
    useLayoutEffect(() => {
        const measure = () => setSizes({sheet: sheetRef.current?.offsetHeight ?? 0, peek: peekRef.current?.offsetHeight ?? 0})
        measure()
        const observer = new ResizeObserver(measure)
        if (sheetRef.current) observer.observe(sheetRef.current)
        if (peekRef.current) observer.observe(peekRef.current)
        return () => observer.disconnect()
    }, [])

    // Выбрали место — показываем его карточку вместо списка
    useEffect(() => {
        if (selected) setExpanded(false)
    }, [selected])

    const distanceTo = (place: MapPlace) => userLocation ? distanceMeters(userLocation, place) : null

    // Если знаем, где пользователь, — ближайшие места сверху
    const items = places
        .map((place) => ({place, distance: distanceTo(place)}))
        .sort((a, b) => (a.distance ?? 0) - (b.distance ?? 0))

    const collapsedOffset = selected ? 0 : Math.max(0, sizes.sheet - sizes.peek)
    const baseOffset = expanded ? 0 : collapsedOffset
    const offset = Math.min(Math.max(baseOffset + dragY, 0), selected ? Infinity : collapsedOffset)

    const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
        dragStart.current = e.clientY
        dragging.current = false
    }

    // Захватываем указатель только когда палец реально поехал — иначе ломаются клики по кнопкам в шапке
    const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
        if (dragStart.current === null) return
        const dy = e.clientY - dragStart.current
        if (!dragging.current) {
            if (Math.abs(dy) < DRAG_START) return
            dragging.current = true
            e.currentTarget.setPointerCapture(e.pointerId)
        }
        setDragY(dy)
    }

    const handlePointerUp = () => {
        if (dragStart.current === null) return
        dragStart.current = null
        if (dragging.current) {
            if (selected) {
                if (dragY > DRAG_THRESHOLD * 2) onDeselect()
            } else if (dragY < -DRAG_THRESHOLD) {
                setExpanded(true)
            } else if (dragY > DRAG_THRESHOLD) {
                setExpanded(false)
            }
        }
        dragging.current = false
        setDragY(0)
    }

    const countText = loading
        ? 'Ищем места…'
        : places.length
            ? `${places.length} ${pluralRu(places.length, ['место', 'места', 'мест'])} в этой области`
            : 'Здесь пока пусто — сдвиньте карту'

    return (
        <>
            <div className={`places-sheet-backdrop${expanded && !selected ? ' is-open' : ''}`} onClick={() => setExpanded(false)} />
            <div
                ref={sheetRef}
                className={`places-sheet${selected ? ' is-detail' : ''}${expanded ? ' is-expanded' : ''}${dragY ? ' is-dragging' : ''}`}
                style={{transform: `translateY(${offset}px)`}}
            >
                <button type="button" className="map-locate" aria-label="Моё местоположение" onClick={onLocate}>
                    <LocateIcon />
                </button>

                <div
                    ref={peekRef}
                    className="places-sheet-header"
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={handlePointerUp}
                >
                    <span className="places-sheet-handle" />
                    {selected ? (
                        <PlaceCard place={selected} distance={distanceTo(selected)} userPoints={userPoints} onClose={onDeselect} />
                    ) : (
                        <>
                            <button type="button" className="places-sheet-title" onClick={() => setExpanded((v) => !v)}>
                                <span className="places-sheet-title-text">
                                    <Text size="l" color="white">Места рядом</Text>
                                    <Text size="xs" color="lightGray">{countText}</Text>
                                </span>
                                <UserPointsPill points={userPoints} />
                                <span className={`places-sheet-chevron${expanded ? ' is-up' : ''}`} aria-hidden="true">
                                    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M4 10l4-4 4 4" />
                                    </svg>
                                </span>
                            </button>
                            <Filters value={filter} onChange={onFilterChange} searchField={searchField} />
                        </>
                    )}
                </div>

                {!selected && (
                    <div className="places-sheet-list">
                        {items.map(({place, distance}) => (
                            <button type="button" key={place.storeId} className="places-sheet-item" onClick={() => onSelect(place)}>
                                <PlaceAvatar title={place.title} image={place.image} />
                                <div className="places-sheet-item-text">
                                    <Text size="m" color="white">{place.title}</Text>
                                    {place.subtitle && <Text size="xs" color="lightGray">{place.subtitle}</Text>}
                                    <div className="places-sheet-item-meta">
                                        <PlaceRating rating={place.rating} />
                                        <PointsChips place={place} />
                                    </div>
                                </div>
                                {distance !== null && (
                                    <span className="places-sheet-distance">{formatDistance(distance)}</span>
                                )}
                            </button>
                        ))}

                        {!loading && !items.length && (
                            <div className="places-sheet-empty">
                                <Text size="m" color="white">Ничего не нашли</Text>
                                <Text size="xs" color="lightGray">
                                    {filter.obtaining || filter.spending
                                        ? 'Попробуйте сбросить фильтры или сдвинуть карту'
                                        : 'Сдвиньте или отдалите карту, чтобы увидеть больше мест'}
                                </Text>
                                {(filter.obtaining || filter.spending) && (
                                    <button type="button" className="map-filter is-active" onClick={() => onFilterChange({obtaining: false, spending: false})}>
                                        Сбросить фильтры
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    )
}
